/**
 * PlatformConsole — Super-Admin (platform owner) SaaS console.
 *
 * Manage operator tenants and review self-serve applications. Guarded by the
 * `is_platform_owner` flag (a platform owner is also an admin). Isolated from
 * the frozen dispatch core — talks only to /api/admin/platform/* endpoints.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Building2, Users, Car, ArrowLeft, RefreshCw, Plus, CheckCircle2, XCircle,
  Inbox, Power, PauseCircle, X, ShieldCheck, Link2, Mail, Phone, MapPin,
  Circle, ClipboardList, Rocket,
} from "lucide-react";

// Read-only setup-wizard step labels (mirror backend ONBOARDING_STEPS order).
const SETUP_STEP_LABELS = {
  profile: "Company profile",
  branding: "Branding",
  booking: "Booking settings",
  area: "Operating area",
  pricing: "Pricing",
  drivers: "Drivers",
  payments: "Payments",
  ai: "AI settings",
  test: "Test booking",
  golive: "Go-live checklist",
};

// Derive a human status from a /setup summary payload.
const setupStatusMeta = (s) => {
  if (!s || !s.summary) return { label: "—", cls: "bg-slate-100 text-slate-400 border-slate-200" };
  const { percent, steps_done, go_live_confirmed } = s.summary;
  if (go_live_confirmed) return { label: "Live", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  if (percent >= 100) return { label: "Ready for go-live", cls: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20" };
  if (!steps_done) return { label: "Not started", cls: "bg-slate-100 text-slate-500 border-slate-200" };
  return { label: "In progress", cls: "bg-amber-100 text-amber-800 border-amber-200" };
};

const STATUS_META = {
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  suspended: { label: "Suspended", cls: "bg-red-100 text-red-700 border-red-200" },
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const Badge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${m.cls}`}>{m.label}</span>;
};

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
      <Icon className="w-4 h-4" style={{ color: accent }} /> {label}
    </div>
    <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
  </div>
);

const CreateOperatorModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Operator name is required"); return; }
    setSaving(true);
    try {
      const clean = Object.fromEntries(Object.entries(form).filter(([, v]) => v && v.trim()));
      const r = await api.post("/admin/platform/operators", { name: form.name, company_name: form.name, ...clean });
      toast.success(`Created ${r.data.operator.name}`);
      onCreated();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="create-operator-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">New operator tenant</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="space-y-3">
          {[
            { k: "name", label: "Operator / company name *", ph: "Acme Cabs Ltd" },
            { k: "contact_name", label: "Contact name", ph: "Jane Doe" },
            { k: "contact_email", label: "Contact email", ph: "ops@acmecabs.com" },
            { k: "contact_phone", label: "Contact phone", ph: "+44…" },
          ].map((f) => (
            <div key={f.k}>
              <label className="text-xs font-semibold text-slate-500">{f.label}</label>
              <input
                data-testid={`new-operator-${f.k}`}
                value={form[f.k]}
                onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))}
                placeholder={f.ph}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button data-testid="create-operator-submit" onClick={submit} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-[#002FA7] text-white hover:bg-[#00238a] disabled:opacity-50">{saving ? "Creating…" : "Create"}</button>
        </div>
      </div>
    </div>
  );
};

export default function PlatformConsole() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("operators");
  const [overview, setOverview] = useState(null);
  const [operators, setOperators] = useState([]);
  const [applications, setApplications] = useState([]);
  const [appFilter, setAppFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [setups, setSetups] = useState({});
  const [checklist, setChecklist] = useState(null); // { op, setup }

  useEffect(() => {
    if (user && !user.is_platform_owner) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ops] = await Promise.all([
        api.get("/admin/platform/overview"),
        api.get("/admin/platform/operators"),
      ]);
      setOverview(ov.data);
      const list = ops.data.operators || [];
      setOperators(list);
      // Read-only: fetch each operator's setup-wizard progress in parallel.
      const results = await Promise.all(
        list.map((o) =>
          api.get(`/admin/platform/operators/${o.id}/setup`)
            .then((r) => [o.id, r.data])
            .catch(() => [o.id, null])
        )
      );
      setSetups(Object.fromEntries(results));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  const loadApps = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/platform/applications?status=${appFilter}`);
      setApplications(r.data.applications || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [appFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (view === "applications") loadApps(); }, [view, loadApps]);

  const setStatus = async (op, status) => {
    try {
      await api.post(`/admin/platform/operators/${op.id}/status`, { status });
      toast.success(`${op.name} → ${status}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const review = async (app, action) => {
    try {
      const r = await api.post(`/admin/platform/applications/${app.id}/${action}`, {});
      toast.success(action === "approve" ? `Provisioned ${r.data.operator?.name}` : "Application rejected");
      loadApps();
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const publicLink = `${window.location.origin}/become-operator`;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-[#4C9AFF]" />
            <div>
              <div className="font-bold tracking-tight">Platform Console</div>
              <div className="text-[11px] text-slate-400">Operator SaaS · super admin</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="platform-back-admin" onClick={() => navigate("/admin")} className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10">
              <ArrowLeft className="w-4 h-4" /> Control Room
            </button>
            <button onClick={logout} className="text-sm text-slate-300 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10">Sign out</button>
          </div>
        </div>
      </header>

      <main data-testid="platform-console" className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard testid="platform-stat-operators" icon={Building2} label="Operators" value={overview?.operators_total ?? 0} accent="#002FA7" />
          <StatCard testid="platform-stat-active" icon={Power} label="Active" value={overview?.operators_by_status?.active ?? 0} accent="#059669" />
          <StatCard testid="platform-stat-suspended" icon={PauseCircle} label="Suspended" value={overview?.operators_by_status?.suspended ?? 0} accent="#dc2626" />
          <StatCard testid="platform-stat-pending-apps" icon={Inbox} label="Pending apps" value={overview?.applications_pending ?? 0} accent="#d97706" />
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-2 border-b border-slate-200">
            <button data-testid="platform-view-operators" onClick={() => setView("operators")} className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 ${view === "operators" ? "border-[#002FA7] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>Operators</button>
            <button data-testid="platform-view-applications" onClick={() => setView("applications")} className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 ${view === "applications" ? "border-[#002FA7] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              Applications
              {(overview?.applications_pending ?? 0) > 0 && <span className="ml-1.5 bg-amber-500 text-white rounded-full px-1.5 py-0.5 text-[10px]">{overview.applications_pending}</span>}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="platform-refresh" onClick={() => (view === "operators" ? loadAll() : loadApps())} className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50"><RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} /></button>
            <button data-testid="platform-new-operator" onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-[#002FA7] text-white hover:bg-[#00238a]"><Plus className="w-4 h-4" /> New operator</button>
          </div>
        </div>

        {view === "operators" && (
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Operator</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-left px-4 py-2 font-semibold">Setup</th>
                  <th className="text-left px-4 py-2 font-semibold">Footprint</th>
                  <th className="text-left px-4 py-2 font-semibold">Contact</th>
                  <th className="text-right px-4 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {operators.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No operators yet.</td></tr>}
                {operators.map((op) => {
                  const setup = setups[op.id];
                  const sm = setupStatusMeta(setup);
                  const pct = setup?.summary?.percent ?? 0;
                  return (
                  <tr key={op.id} data-testid={`operator-row-${op.slug}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        {op.name}
                        {op.is_founding && <span className="text-[10px] bg-[#002FA7]/10 text-[#002FA7] px-1.5 py-0.5 rounded font-bold">FOUNDING</span>}
                      </div>
                      <div className="text-xs text-slate-400">{op.slug}</div>
                    </td>
                    <td className="px-4 py-3"><Badge status={op.status} /></td>
                    <td className="px-4 py-3" data-testid={`operator-setup-${op.slug}`}>
                      <button
                        onClick={() => setup && setChecklist({ op, setup })}
                        disabled={!setup}
                        className="group text-left disabled:cursor-default"
                        data-testid={`operator-setup-open-${op.slug}`}
                        title={setup ? "View setup checklist" : ""}
                      >
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${sm.cls}`} data-testid={`operator-setup-status-${op.slug}`}>
                          {sm.label}
                        </span>
                        <div className="flex items-center gap-2 mt-1 w-32">
                          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : "bg-[#002FA7]"}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] tabular-nums text-slate-500 group-hover:text-slate-700" data-testid={`operator-setup-pct-${op.slug}`}>{pct}%</span>
                        </div>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <span className="inline-flex items-center gap-1 mr-3"><Users className="w-3.5 h-3.5 text-slate-400" />{op.counts?.users ?? 0}</span>
                      <span className="inline-flex items-center gap-1"><Car className="w-3.5 h-3.5 text-slate-400" />{op.counts?.rides ?? 0}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{op.contact_email || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {op.is_founding ? (
                        <span className="text-xs text-slate-400">Protected</span>
                      ) : op.status === "active" ? (
                        <button data-testid={`operator-suspend-${op.slug}`} onClick={() => setStatus(op, "suspended")} className="text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50">Suspend</button>
                      ) : (
                        <button data-testid={`operator-activate-${op.slug}`} onClick={() => setStatus(op, "active")} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded hover:bg-emerald-50">Activate</button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {view === "applications" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                {["pending", "approved", "rejected", "all"].map((s) => (
                  <button key={s} data-testid={`app-filter-${s}`} onClick={() => setAppFilter(s)} className={`px-3 py-1.5 text-xs font-medium capitalize ${appFilter === s ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{s}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Link2 className="w-4 h-4" />
                <span>Public link:</span>
                <button data-testid="copy-public-link" onClick={() => { navigator.clipboard?.writeText(publicLink); toast.success("Application link copied"); }} className="font-mono text-[#002FA7] hover:underline">{publicLink}</button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {applications.length === 0 && <div className="col-span-full text-center text-slate-400 py-8 text-sm">No applications.</div>}
              {applications.map((a) => (
                <div key={a.id} data-testid={`application-card-${a.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-bold text-slate-900">{a.company_name}</div>
                      <div className="text-xs text-slate-500">{a.contact_name}</div>
                    </div>
                    <Badge status={a.status} />
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-slate-600">
                    {a.contact_email && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" />{a.contact_email}</div>}
                    {a.contact_phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" />{a.contact_phone}</div>}
                    {(a.region || a.fleet_size) && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" />{[a.region, a.fleet_size && `${a.fleet_size} vehicles`].filter(Boolean).join(" · ")}</div>}
                    {a.message && <div className="text-slate-500 italic mt-1">“{a.message}”</div>}
                  </div>
                  {a.status === "pending" && (
                    <div className="flex gap-2 mt-3">
                      <button data-testid={`app-approve-${a.id}`} onClick={() => review(a, "approve")} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4" /> Approve &amp; provision</button>
                      <button data-testid={`app-reject-${a.id}`} onClick={() => review(a, "reject")} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"><XCircle className="w-4 h-4" /></button>
                    </div>
                  )}
                  {a.status === "approved" && a.operator_id && <div className="mt-2 text-xs text-emerald-700">Tenant provisioned.</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {showCreate && <CreateOperatorModal onClose={() => setShowCreate(false)} onCreated={loadAll} />}
      {checklist && <SetupChecklistDrawer op={checklist.op} setup={checklist.setup} onClose={() => setChecklist(null)} />}
    </div>
  );
}

// Read-only per-operator setup checklist. No editing — Platform Owner view only.
const SetupChecklistDrawer = ({ op, setup, onClose }) => {
  const stepIds = setup?.step_ids || Object.keys(SETUP_STEP_LABELS);
  const progress = setup?.progress || {};
  const sm = setupStatusMeta(setup);
  const pct = setup?.summary?.percent ?? 0;
  const done = setup?.summary?.steps_done ?? 0;
  const total = setup?.summary?.steps_total ?? stepIds.length;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" data-testid="operator-setup-drawer" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
              <ClipboardList className="w-3.5 h-3.5" /> Setup checklist · read-only
            </div>
            <h3 className="text-lg font-bold text-slate-900 mt-0.5">{op.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${sm.cls}`} data-testid="drawer-setup-status">{sm.label}</span>
              <span className="text-xs text-slate-500 tabular-nums" data-testid="drawer-setup-pct">{done} / {total} · {pct}%</span>
            </div>
          </div>
          <button data-testid="operator-setup-drawer-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="px-5 py-3">
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-4">
            <div className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : "bg-[#002FA7]"}`} style={{ width: `${pct}%` }} />
          </div>
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {stepIds.map((id, i) => {
              const stepDone = !!progress[id]?.done;
              return (
                <li key={id} className="flex items-center gap-2 px-3 py-2.5 text-sm" data-testid={`drawer-step-${id}`}>
                  <span className="text-[11px] tabular-nums text-slate-400 w-4">{i + 1}</span>
                  {stepDone
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    : <Circle className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                  <span className="flex-1 text-slate-700">{SETUP_STEP_LABELS[id] || id}</span>
                  <span className={`text-[11px] font-semibold ${stepDone ? "text-emerald-700" : "text-slate-400"}`}>{stepDone ? "DONE" : "TO DO"}</span>
                </li>
              );
            })}
          </ul>
          {setup?.summary?.go_live_confirmed && (
            <div className="mt-4 flex items-start gap-2 border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-lg px-3 py-2 text-xs" data-testid="drawer-golive-confirmed">
              <Rocket className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>This operator has confirmed go-live and is ready to operate.</span>
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-4">
            This is a read-only view. Only the operator's own admins can edit their setup from their control room.
          </p>
        </div>
      </div>
    </div>
  );
}
